import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { ComponentType } from "@angular/cdk/portal";
import { LinkedInWindow } from "./onboarding.interface";
import { Router, ActivatedRoute } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { ToastrService } from "../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import { MatDialog } from "@angular/material/dialog";
import { LinkAddComponent } from "../addons/links/link-add/link-add.component";
import { LinkEditComponent } from "../addons/links/link-edit/link-edit.component";
import { BannerAddComponent } from "../addons/banners/banner-add/banner-add.component";
import { BannerEditComponent } from "../addons/banners/banner-edit/banner-edit.component";
import { DisclaimerAddComponent } from "../addons/disclaimers/disclaimer-add/disclaimer-add.component";
import { DisclaimerEditComponent } from "../addons/disclaimers/disclaimer-edit/disclaimer-edit.component";
import {
  ADD_BANNER_SUCCESS,
  CHANGE_BANNER_SUCCESS,
} from "../addons/banners/banners.actions";
import {
  ADD_LINK_SUCCESS,
  CHANGE_LINK_SUCCESS,
} from "../addons/links/links.actions";
import {
  ADD_DISCLAIMER_SUCCESS,
  CHANGE_DISCLAIMER_SUCCESS,
} from "../addons/disclaimers/disclaimers.actions";
import { CustomIconAddComponent } from "./custom-icon-add/custom-icon-add.component";
import { SocialMediaComponent } from "../shared/social-media/social-media.component";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { ChangeAccountAction } from "../accounts/accounts.actions";
import { AuthService } from "../auth/auth.service";
import { Subscription } from "rxjs";
import { OnboardingService } from "./onboarding.service";
import { OnboardingSessionData } from "./onboarding.interface";
import { generateSignatureTemplate } from "./signature-template";
import {
  OnboardingStep,
  EmployeeData,
  SocialMedia,
  AvailableAddons,
  SelectedAddons,
  SignatureTemplateData,
} from "./onboarding.interface";
import {
  SOCIAL_MEDIA_NAMES,
  generateSocialMediaIconHtml,
} from "../shared/social-media/social-media.data";
import {
  SocialMediaData,
  SocialMediaIcon,
} from "../shared/social-media/social-media.interface";
import {
  getBrandLogoPath,
  isDbc1Brand,
} from "../shared/branding/branding.util";
import { ALLOWED_RASTER_IMAGE_MIME_TYPES } from "../shared/image-uploader/image.interface";

@Component({
  selector: "app-onboarding",
  templateUrl: "./onboarding.component.html",
  styleUrls: ["./onboarding.component.scss"],
  standalone: false,
})
export class OnboardingComponent implements OnInit, OnDestroy {
  readonly rasterMimeTypes = ALLOWED_RASTER_IMAGE_MIME_TYPES;
  readonly logoLight = getBrandLogoPath("black");
  readonly logoDark = getBrandLogoPath("white");
  readonly appBaseUrl = isDbc1Brand()
    ? "https://app.dbc1.com"
    : "https://app.gsignature.com";

  get completionItems() {
    return [
      { text: "onboarding.step4.domainCreated", value: this.domain },
      {
        text: "onboarding.step4.employeeCreated",
        value: this.employeeData.email,
      },
      { text: "onboarding.step4.signaturePrepared" },
    ];
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private toastr: ToastrService,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
    private onboardingService: OnboardingService,
    private dialog: MatDialog,
    private store: Store,
    private authService: AuthService,
    private actions$: Actions,
  ) {
    this.actions$
      .pipe(
        ofType(
          ADD_BANNER_SUCCESS,
          CHANGE_BANNER_SUCCESS,
          ADD_LINK_SUCCESS,
          CHANGE_LINK_SUCCESS,
          ADD_DISCLAIMER_SUCCESS,
          CHANGE_DISCLAIMER_SUCCESS,
        ),
      )
      .subscribe(() => {
        void this.loadAvailableAddons();
      });
  }
  currentStep = 1;
  totalSteps = 5;
  email = "";
  domain = "";
  isLoading = false;
  contactInfoLoaded = false;
  colorsLogoLoaded = false;
  signatureHtml: SafeHtml = "";
  signatureHtmlWithAddons: SafeHtml = "";
  onboardingData: OnboardingSessionData | null = null;

  steps: OnboardingStep[] = [
    {
      id: 1,
      title: "Fetching data",
      completed: false,
      active: true,
      skipped: false,
    },
    {
      id: 2,
      title: "Basic info",
      completed: false,
      active: false,
      skipped: false,
    },
    {
      id: 3,
      title: "Social Icons",
      completed: false,
      active: false,
      skipped: false,
    },
    { id: 4, title: "Addons", completed: false, active: false, skipped: false },
    {
      id: 5,
      title: "Review & Complete",
      completed: false,
      active: false,
      skipped: false,
    },
  ];

  employeeData: EmployeeData = {
    givenName: "John",
    familyName: "Kowalsky",
    email: "",
    jobTitle: "Key Account Manager",
    phone: "+1 234 121 7878",
    profilePicture:
      "https://storage.googleapis.com/prod_gsignature_user_images/generator.gs.com/media/images/2025-07-01T12:50:40.249Z_c0103529-c881-4dea-beab-0d8593ad4a02",
  };

  socialMedia: SocialMedia = {
    facebook: "https://facebook.com/",
    instagram: "https://instagram.com/",
    linkedin: "https://linkedin.com/in/",
    twitter: "https://twitter.com/",
  };

  socialMediaIcons: SocialMediaIcon[] = [];
  selectedSocialMediaIcons: SocialMediaIcon[] = [];
  showAllSocialIcons = false;
  brandColor = "#000000";
  availableAddons: AvailableAddons = {
    links: [],
    banners: [],
    disclaimers: [],
  };
  selectedAddons: SelectedAddons = {
    linkId: null,
    bannerId: null,
    additionalBannerId: null,
    disclaimerId: null,
  };

  companyLogo = "";
  companyName = "";
  companyAddress = "";
  companyWebsite = "";
  private domainCreated = false;
  private authSubscription: Subscription;
  isEmailLocked = false;
  showAdditionalBannerSection = false;

  basicInfoFields = [
    {
      label: "onboarding.step1.email",
      model: "email",
      type: "email",
      placeholder: "onboarding.step1.enterEmail",
      disabled: true,
    },
    {
      label: "onboarding.step1.givenName",
      model: "givenName",
      type: "text",
      placeholder: "onboarding.step1.enterGivenName",
    },
    {
      label: "onboarding.step1.familyName",
      model: "familyName",
      type: "text",
      placeholder: "onboarding.step1.enterFamilyName",
    },
    {
      label: "onboarding.step1.jobTitle",
      model: "jobTitle",
      type: "text",
      placeholder: "onboarding.step1.enterJobTitle",
    },
    {
      label: "onboarding.step1.phoneNumber",
      model: "phone",
      type: "tel",
      placeholder: "onboarding.step1.enterPhoneNumber",
    },
  ];

  socialFields = [
    {
      label: "Facebook",
      key: "facebook",
      placeholder: "https://facebook.com/yourprofile",
    },
    {
      label: "X / Twitter",
      key: "twitter",
      placeholder: "https://twitter.com/yourprofile",
    },
    {
      label: "LinkedIn",
      key: "linkedin",
      placeholder: "https://linkedin.com/in/yourprofile",
    },
    {
      label: "Instagram",
      key: "instagram",
      placeholder: "https://instagram.com/yourprofile",
    },
  ];

  addonTypes = [
    {
      title: "Link",
      icon: "link",
      key: "linkId",
      plural: "links",
      addMethod: () => this.showAddLinkForm(),
    },
    {
      title: "Banner",
      icon: "image",
      key: "bannerId",
      plural: "banners",
      addMethod: () => this.showAddBannerForm(),
    },
    {
      title: "Disclaimer",
      icon: "description",
      key: "disclaimerId",
      plural: "disclaimers",
      addMethod: () => this.showAddDisclaimerForm(),
    },
  ];

  @ViewChild("socialMediaComponent")
  socialMediaComponent!: SocialMediaComponent;

  ngOnInit() {
    this.initializeLanguage();
    this.setupRouteHandling();
    this.setupAuthSubscription();
    void this.initializeServices();
    this.loadLinkedInInsightTag();
  }

  private initializeLanguage(): void {
    this.translate.setDefaultLang("en");
    const browserLang = this.translate.getBrowserLang();
    const defaultLang = browserLang?.match(/en|es|pl/) ? browserLang : "en";
    this.translate.use(defaultLang);
  }

  private loadLinkedInInsightTag(): void {
    const win = window as unknown as LinkedInWindow;
    if (typeof win.lintrk === "function") {
      return;
    }

    win._linkedin_partner_id = "514166306";
    win._linkedin_data_partner_ids = win._linkedin_data_partner_ids || [];
    win._linkedin_data_partner_ids.push("514166306");

    win.lintrk = function (a: string, b: unknown) {
      ((win.lintrk as unknown as { q: unknown[] }).q =
        (win.lintrk as unknown as { q: unknown[] }).q || []).push([a, b]);
    };
    (win.lintrk as unknown as { q: unknown[] }).q = [];

    const script = document.createElement("script");
    script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    script.async = true;
    document.head.appendChild(script);
  }

  private trackLinkedInConversion(): void {
    const win = window as unknown as LinkedInWindow;
    if (typeof win.lintrk === "function") {
      win.lintrk("track", { conversion_id: 24146001 });
    }
  }

  private setupRouteHandling(): void {
    this.route.queryParams.subscribe((params) => {
      this.currentStep = 1;
      this.steps[0].active = true;
      this.steps[1].active = false;

      const paramsDomain = params.domain as string | undefined;
      const paramsEmail = params.email as string | undefined;
      this.domain = paramsDomain || this.domain;
      this.email = paramsEmail || this.email;

      if (params.domain || params.data) {
        this.trackLinkedInConversion();
      }

      this.isEmailLocked = !!(params.email || params.data);
      this.updateEmailFieldState();

      if (params.data) {
        this.onboardingData = JSON.parse(
          atob(params.data as string),
        ) as OnboardingSessionData;
        this.domain = this.onboardingData.domain || "";
        this.email = this.onboardingData.googleAdminEmail || "";
        this.employeeData.email = this.email;

        if (this.onboardingData.givenName) {
          this.employeeData.givenName = this.onboardingData.givenName;
        }
        if (this.onboardingData.familyName) {
          this.employeeData.familyName = this.onboardingData.familyName;
        }
        if (this.onboardingData.profilePicture) {
          this.employeeData.profilePicture = this.onboardingData.profilePicture;
        }

        this.validateEmailDomain();
      }

      void this.startContactInfoStep();
    });
  }

  private setupAuthSubscription(): void {
    this.authSubscription = this.authService.authState$.subscribe((user) => {
      if (user && !this.email) {
        this.employeeData.email = user.email;
        this.email = user.email;
        this.validateEmailDomain();
        if (!this.domain) {
          this.domain = user.email.split("@")[1];
        }
      }
    });
  }

  private async initializeServices(): Promise<void> {
    this.initializeDefaultSelectedIcons();
    this.updateSignaturePreview();

    if (this.domain) {
      await Promise.all([
        this.loadAvailableAddons(),
        this.loadCompanyData(),
        this.loadBrandColor(),
      ]);
      this.updateSignaturePreview();
    }
  }

  private initializeDefaultSelectedIcons(): void {
    if (!this.selectedSocialMediaIcons.length) {
      this.selectedSocialMediaIcons = [
        "facebook",
        "linkedin",
        "twitter",
        "instagram",
      ].map((platform) => ({
        name: platform.charAt(0).toUpperCase() + platform.slice(1),
        platform,
        imageUrl: "",
        alt: platform,
        selected: true,
        url: this.socialMedia[platform as keyof SocialMedia],
      }));
    }
  }

  private async loadBrandColor(): Promise<void> {
    try {
      const response = await this.http
        .get<{ brandColor?: string }>(`/onboarding/${this.domain}/brand-color`)
        .toPromise();
      this.brandColor = response?.brandColor || "#000000";
    } catch {
      this.brandColor = "#000000";
    }
  }

  async startContactInfoStep(): Promise<void> {
    this.isLoading = true;
    try {
      if (!this.domain) {
        return;
      }

      this.ensureDomainAccountContext(this.domain);

      if (this.onboardingData && !this.domainCreated) {
        await this.onboardingService
          .createDomainOnboarding(this.domain, this.onboardingData)
          .toPromise();
        this.ensureDomainAccountContext(this.domain);
      }
      this.domainCreated = true;
      this.contactInfoLoaded = true;

      try {
        await this.onboardingService
          .enrichWithBrandData(this.domain)
          .toPromise();
      } catch {
        // Brand enrich is best-effort for existing domains (e.g. from Start).
      }
      this.colorsLogoLoaded = true;

      await Promise.all([
        this.loadCompanyData(),
        this.loadAvailableAddons(),
        this.loadBrandColor(),
      ]);

      this.completeStep(1);
      this.startBasicInfoStep();
    } catch {
      this.toastr.error(
        this.translate.instant("onboarding.error.contactInfo") as string,
      );
    } finally {
      this.isLoading = false;
    }
  }

  private ensureDomainAccountContext(domain: string): void {
    this.store.dispatch(new ChangeAccountAction(domain));
  }

  startBasicInfoStep(): void {
    this.currentStep = 2;
    this.steps[0].active = false;
    this.steps[1].active = true;

    if (this.domain) {
      this.store.dispatch(new ChangeAccountAction(this.domain));

      if (this.steps[0].completed) {
        this.contactInfoLoaded = true;
        this.colorsLogoLoaded = true;
      }
    }
  }

  completeStep(stepNumber: number): void {
    const stepIndex = stepNumber - 1;
    this.steps[stepIndex].completed = true;
    this.steps[stepIndex].active = false;

    if (stepNumber < this.totalSteps) {
      this.steps[stepIndex + 1].active = true;
      this.currentStep = stepNumber + 1;
    }
  }

  nextStep(): void {
    if (this.currentStep === 2) {
      this.completeStep(2);
      this.currentStep = 3;
      this.steps[2].active = true;
    } else if (this.currentStep === 3) {
      this.completeStep(3);
      this.currentStep = 4;
      this.steps[3].active = true;
    } else if (this.currentStep < this.totalSteps) {
      this.completeStep(this.currentStep);
    }
  }

  canProceedToNextStep(): boolean {
    switch (this.currentStep) {
      case 1:
        return this.contactInfoLoaded && this.colorsLogoLoaded;
      case 2:
        return (
          !!this.employeeData.givenName &&
          !!this.employeeData.email &&
          this.employeeData.email.trim() !== ""
        );
      default:
        return true;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.steps[this.currentStep - 1].active = false;
      this.steps[this.currentStep - 2].active = true;
      this.currentStep--;
    }
  }

  async saveAndInstallLater(): Promise<void> {
    this.isLoading = true;
    try {
      const onboardingData = {
        employeeData: this.employeeData,
        addons: {
          linkId: this.selectedAddons.linkId || null,
          bannerId: this.selectedAddons.bannerId || null,
          additionalBannerId: this.selectedAddons.additionalBannerId || null,
          disclaimerId: this.selectedAddons.disclaimerId || null,
        },
        signatureHtml: this.generateFinalSignatureHtmlWithAddons(),
        socialMediaIcons: this.generateSocialMediaIconsForBackend(),
        installLater: true,
        googleAdminEmail: this.email || this.employeeData.email || undefined,
      };

      const response = await this.http
        .post(`/onboarding/${this.domain}/save-progress`, onboardingData)
        .toPromise();

      const result = response as { step?: string } | undefined;
      if (result && result.step === "skipped") {
        this.steps[4].completed = false;
        this.steps[4].active = false;
        this.steps[4].skipped = true;
        if (this.authService.appUser) {
          this.authService.appUser.onboarding = "SKIPPED";
        }
      }

      void this.router.navigate(["/start"]);
    } catch {
      this.toastr.error(
        this.translate.instant("onboarding.error.loggingIn") as string,
      );
      void this.router.navigate(["/start"]);
    } finally {
      this.isLoading = false;
    }
  }

  async completeOnboarding(): Promise<void> {
    this.isLoading = true;
    try {
      const onboardingData = {
        employeeData: this.employeeData,
        addons: {
          linkId: this.selectedAddons.linkId || null,
          bannerId: this.selectedAddons.bannerId || null,
          additionalBannerId: this.selectedAddons.additionalBannerId || null,
          disclaimerId: this.selectedAddons.disclaimerId || null,
        },
        signatureHtml: this.generateFinalSignatureHtmlWithAddons(),
        socialMediaIcons: this.generateSocialMediaIconsForBackend(),
        installLater: false,
        googleAdminEmail: this.email || this.employeeData.email || undefined,
      };

      const response = await this.http
        .post(`/onboarding/${this.domain}/save-progress`, onboardingData)
        .toPromise();

      const result = response as { step?: string } | undefined;
      if (result && result.step === "completed") {
        this.completeStep(5);
        this.toastr.success(
          this.translate.instant("onboarding.success.completed") as string,
        );
        if (this.authService.appUser) {
          this.authService.appUser.onboarding = "COMPLETED";
        }
      }

      void this.router.navigate(["/start"]);
    } catch {
      this.toastr.error(
        this.translate.instant("onboarding.error.completion") as string,
      );
      void this.router.navigate(["/start"]);
    } finally {
      this.isLoading = false;
    }
  }

  updateSignaturePreview(): void {
    this.signatureHtml = this.sanitizer.bypassSecurityTrustHtml(
      DOMPurify.sanitize(this.generateSignatureHtml() ?? "", {
        ALLOWED_TAGS: [
          "a",
          "b",
          "br",
          "em",
          "i",
          "img",
          "p",
          "span",
          "strong",
          "table",
          "td",
          "tr",
          "div",
          "ul",
          "ol",
          "li",
        ],
      }),
    );
    this.updateSignaturePreviewWithAddons();
  }

  onFieldChange(fieldModel: string): void {
    if (fieldModel === "email") {
      this.validateEmailDomain();
    }
    this.updateSignaturePreview();
  }

  private validateEmailDomain(): void {
    if (this.employeeData.email && this.domain) {
      const emailDomain = this.employeeData.email.split("@")[1];
      if (
        emailDomain &&
        emailDomain.toLowerCase() !== this.domain.toLowerCase()
      ) {
        const emailPrefix = this.employeeData.email.split("@")[0];
        this.employeeData.email = `${emailPrefix}@${this.domain}`;
      }
    }
  }

  private generateSignatureHtml(): string {
    const data = this.employeeData;
    const fullName = `${data.givenName} ${data.familyName}`;

    const socialMediaIcons = this.generateSocialMediaIconsForPreview();

    const templateData: SignatureTemplateData = {
      userName: fullName,
      jobTitle: data.jobTitle || "",
      userEmail: data.email || "",
      phoneNumber: data.phone || "",
      userThumbnail:
        data.profilePicture && data.profilePicture.trim()
          ? `<img src="${data.profilePicture}" width="80" height="80" alt="Profile Picture" style="display: block; width: 80px; height: 80px; border-radius: 50%;">`
          : "",
      companyImage: this.companyLogo
        ? this.companyWebsite
          ? `<a href="${this.companyWebsite.startsWith("http") ? this.companyWebsite : `https://${this.companyWebsite}`}" target="_blank"><img src="${this.companyLogo}" alt="Company" width="100" style="display: block; width: 100px; height: auto;"></a>`
          : `<img src="${this.companyLogo}" alt="Company" width="100" style="display: block; width: 100px; height: auto;">`
        : "",
      socialMediaIcons,
      website: this.domain || "",
      companyName: this.companyName,
      companyAddress: this.companyAddress,
    };

    return generateSignatureTemplate(templateData);
  }

  private generateFinalSignatureHtml(): string {
    const data = this.employeeData;
    const fullName = `${data.givenName} ${data.familyName}`;

    const templateData: SignatureTemplateData = {
      userName: fullName ? "${userName}" : "",
      jobTitle: data.jobTitle && data.jobTitle.trim() ? "${jobTitle}" : "",
      userEmail: data.email && data.email.trim() ? "${userEmail}" : "",
      phoneNumber: data.phone && data.phone.trim() ? "${phoneNumber}" : "",
      userThumbnail:
        data.profilePicture && data.profilePicture.trim()
          ? "${userThumbnail}"
          : "",
      companyImage: this.companyLogo ? "${companyImage}" : "",
      socialMediaIcons: this.generateSocialMediaIconsForPreview(),
      website: this.domain || "",
      companyName: this.companyName ? "${companyName}" : "",
      companyAddress: this.companyAddress ? "${companyAddress}" : "",
    };

    return generateSignatureTemplate(templateData);
  }

  private generateFinalSignatureHtmlWithAddons(): string {
    return this.generateFinalSignatureHtml();
  }

  onProfileImageChange(image: { previewUrl?: string }): void {
    this.employeeData.profilePicture = image.previewUrl;
    this.updateSignaturePreview();
  }

  removeProfilePicture(): void {
    this.employeeData.profilePicture = undefined;
    this.updateSignaturePreview();
  }

  async loadCompanyData(): Promise<void> {
    try {
      const response = await this.onboardingService
        .getCompanyData(this.domain)
        .toPromise();
      if (response) {
        this.companyName = response.name || "";
        this.companyAddress = response.address || "";
        this.companyWebsite = response.website || "";
        if (response.logoUrl) {
          this.companyLogo = response.logoUrl;
        }
        this.updateSignaturePreview();
      }
    } catch {
      this.toastr.warning("Failed to load company data");
    }
  }

  async loadAvailableAddons(): Promise<void> {
    if (!this.domain) {
      return;
    }

    try {
      const timestamp = new Date().getTime();
      const response = await this.http
        .get(`/onboarding/${this.domain}/addons?t=${timestamp}`)
        .toPromise();
      this.availableAddons = response as AvailableAddons;
      this.autoSelectBrandAddons();
      this.updateSignaturePreview();
    } catch {
      this.availableAddons = {
        links: [],
        banners: [],
        disclaimers: [],
      };
    }
  }

  private autoSelectBrandAddons(): void {
    const brandDisclaimers =
      this.availableAddons.disclaimers?.filter(
        (addon) => addon.data?.name === "Disclaimer",
      ) || [];

    const brandBanners =
      this.availableAddons.banners?.filter(
        (addon) => addon.data?.name === "Banner",
      ) || [];

    const brandLinks =
      this.availableAddons.links?.filter(
        (addon) => addon.data?.name === "Book a Meeting",
      ) || [];

    if (brandDisclaimers.length > 0) {
      this.selectedAddons.disclaimerId = brandDisclaimers[0].addonId;
    }
    if (brandBanners.length > 0) {
      this.selectedAddons.bannerId = brandBanners[0].addonId;
    }
    if (brandLinks.length > 0) {
      this.selectedAddons.linkId = brandLinks[0].addonId;
    }
  }

  onAddonChange(): void {
    this.updateSignaturePreviewWithAddons();
  }

  selectExistingAddon(type: string, addon: unknown): void {
    switch (type) {
      case "LINK":
        this.selectedAddons.linkId = (addon as { addonId: string }).addonId;
        break;
      case "BANNER":
        this.selectedAddons.bannerId = (addon as { addonId: string }).addonId;
        break;
      case "DISCLAIMER":
        this.selectedAddons.disclaimerId = (
          addon as { addonId: string }
        ).addonId;
        break;
    }

    this.onAddonChange();
    this.toastr.success(
      `Selected existing ${type.toLowerCase()}: ${
        (addon as { data?: { name?: string } }).data?.name || "Unnamed"
      }`,
    );
  }

  showAddLinkForm(): void {
    this.openAddonDialog(LinkAddComponent);
  }

  showAddBannerForm(): void {
    this.openAddonDialog(BannerAddComponent);
  }

  showAddDisclaimerForm(): void {
    this.openAddonDialog(DisclaimerAddComponent);
  }

  showAddAdditionalBannerForm(): void {
    this.openAddonDialog(BannerAddComponent);
  }

  toggleAdditionalBannerSection(): void {
    this.showAdditionalBannerSection = !this.showAdditionalBannerSection;

    if (this.showAdditionalBannerSection) {
      const bannerIndex = this.addonTypes.findIndex(
        (addon) => addon.key === "bannerId",
      );
      if (bannerIndex !== -1) {
        this.addonTypes.splice(bannerIndex + 1, 0, {
          title: "Additional Banner",
          icon: "image",
          key: "additionalBannerId",
          plural: "banners",
          addMethod: () => this.showAddAdditionalBannerForm(),
        });
      }
    } else {
      this.addonTypes = this.addonTypes.filter(
        (addon) => addon.key !== "additionalBannerId",
      );
    }
  }

  hideAdditionalBannerSection(): void {
    this.showAdditionalBannerSection = false;
    this.addonTypes = this.addonTypes.filter(
      (addon) => addon.key !== "additionalBannerId",
    );
    this.selectedAddons.additionalBannerId = null;
    this.onAddonChange();
  }

  showAddCustomIconForm(): void {
    const dialogRef = this.dialog.open(CustomIconAddComponent, {
      width: "600px",
      maxWidth: "92vw",
      disableClose: true,
      panelClass: "app-dialog-panel",
    });

    dialogRef
      .afterClosed()
      .subscribe((result: (SocialMediaIcon & { file?: File }) | undefined) => {
        if (result) {
          if (result.file) {
            void this.uploadCustomIconImage(result.file, result);
          } else {
            this.socialMediaComponent.addCustomIcon(result);
          }
        }
      });
  }

  onSocialMediaChange(socialMediaData: SocialMediaData): void {
    this.socialMedia = socialMediaData;
    this.updateSignaturePreview();
  }

  onEmployeeDataChange(employeeData: EmployeeData): void {
    this.employeeData = employeeData;
    this.updateSignaturePreview();
  }

  onDomainChange(domain: string): void {
    this.domain = domain;
    this.updateSignaturePreview();
  }

  onSelectedIconsChange(selectedIcons: SocialMediaIcon[]): void {
    this.selectedSocialMediaIcons = selectedIcons;
    this.updateSignaturePreview();
  }

  onShowAllSocialIconsChange(showAll: boolean): void {
    this.showAllSocialIcons = showAll;
  }

  private generateSocialMediaIconsForPreview(): SocialMediaIcon[] {
    const socialIcons = this.selectedSocialMediaIcons.map((icon) =>
      this.createIconForPreview(icon),
    );
    const contactIcons = this.generateContactIcons();

    return [...socialIcons, ...contactIcons];
  }

  private createIconForPreview(icon: SocialMediaIcon): SocialMediaIcon {
    const url = this.getIconUrl(icon);
    return {
      ...icon,
      imageUrl: icon.platform.startsWith("custom_")
        ? icon.imageUrl
        : generateSocialMediaIconHtml(icon.platform, this.brandColor, "small"),
      url: this.ensureHttps(url),
    };
  }

  private generateContactIcons(): SocialMediaIcon[] {
    const contactData = [
      {
        platform: "phone",
        data: this.employeeData.phone,
        url: (d: string) => `tel:${d}`,
      },
      {
        platform: "email",
        data: this.employeeData.email,
        url: (d: string) => `mailto:${d}`,
      },
      {
        platform: "www",
        data: this.domain,
        url: (d: string) => `https://${d}`,
      },
    ];

    return contactData
      .filter(({ data }) => data?.trim())
      .map(({ platform, data, url }) => ({
        name: SOCIAL_MEDIA_NAMES[platform as keyof typeof SOCIAL_MEDIA_NAMES],
        platform,
        imageUrl: generateSocialMediaIconHtml(
          platform,
          this.brandColor,
          "small",
        ),
        alt: platform,
        url: url(data),
      }));
  }

  private getIconUrl(icon: SocialMediaIcon): string {
    return icon.platform.startsWith("custom_")
      ? icon.url || ""
      : this.socialMedia[icon.platform as keyof SocialMedia] || "";
  }

  private generateSocialMediaIconsForBackend(): Array<{
    platform: string;
    url: string;
    isCustom?: boolean;
    customImageUrl?: string;
  }> {
    const socialIcons = this.selectedSocialMediaIcons.map((icon) =>
      this.createIconForBackend(icon),
    );
    const contactIcons = this.generateContactIconsForBackend();

    return [...socialIcons, ...contactIcons];
  }

  private createIconForBackend(icon: SocialMediaIcon): {
    platform: string;
    url: string;
    isCustom?: boolean;
    customImageUrl?: string;
  } {
    return {
      platform: icon.platform,
      url: this.ensureHttps(this.getIconUrl(icon)),
      isCustom: icon.platform.startsWith("custom_"),
      customImageUrl: icon.platform.startsWith("custom_")
        ? icon.imageUrl
        : undefined,
    };
  }

  private generateContactIconsForBackend(): Array<{
    platform: string;
    url: string;
    isCustom?: boolean;
    customImageUrl?: string;
  }> {
    const contactData = [
      {
        platform: "phone",
        data: this.employeeData.phone,
        url: (d: string) => `tel:${d}`,
      },
      {
        platform: "email",
        data: this.employeeData.email,
        url: (d: string) => `mailto:${d}`,
      },
      {
        platform: "www",
        data: this.domain,
        url: (d: string) => `https://${d}`,
      },
    ];

    return contactData
      .filter(({ data }) => data?.trim())
      .map(({ platform, data, url }) => ({
        platform,
        url: url(data),
        isCustom: false,
        customImageUrl: undefined,
      }));
  }

  private openAddonDialog(component: ComponentType<unknown>): void {
    const dialogRef = this.dialog.open(component, {
      width: "600px",
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  private async uploadCustomIconImage(
    file: File,
    iconData: SocialMediaIcon,
  ): Promise<void> {
    try {
      const formData = new FormData();
      formData.append("image", file);
      formData.append("name", iconData.name);

      const response = await this.http
        .post<{ imageUrl: string }>("/media/images", formData)
        .toPromise();

      if (response && response.imageUrl) {
        iconData.imageUrl = response.imageUrl;
        this.socialMediaComponent.addCustomIcon(iconData);
      }
    } catch {
      this.socialMediaComponent.addCustomIcon(iconData);
    }
  }

  updateSignaturePreviewWithAddons(): void {
    this.signatureHtmlWithAddons = this.sanitizer.bypassSecurityTrustHtml(
      DOMPurify.sanitize(this.generateSignatureHtmlWithAddons() ?? "", {
        ALLOWED_TAGS: [
          "a",
          "b",
          "br",
          "em",
          "i",
          "img",
          "p",
          "span",
          "strong",
          "table",
          "td",
          "tr",
          "div",
          "ul",
          "ol",
          "li",
        ],
      }),
    );
  }

  private generateSignatureHtmlWithAddons(): string {
    let html = this.generateSignatureHtml();

    if (this.selectedAddons.linkId) {
      const link = this.availableAddons.links.find(
        (l) => l.addonId === this.selectedAddons.linkId,
      );
      if (link) {
        const url = typeof link.data.url === "string" ? link.data.url : "";
        const text =
          typeof link.data.text === "string"
            ? link.data.text
            : typeof link.data.url === "string"
              ? link.data.url
              : "";
        html += `<div style="margin-top: 10px;"><a href="${url}" target="_blank" style="#007bff; text-decoration: none;">${text}</a></div>`;
      }
    }

    if (this.selectedAddons.bannerId) {
      const banner = this.availableAddons.banners.find(
        (b) => b.addonId === this.selectedAddons.bannerId,
      );
      if (banner) {
        const imageWidth =
          typeof banner.data.imageWidth === "number" ||
          typeof banner.data.imageWidth === "string"
            ? String(banner.data.imageWidth)
            : "auto";
        const imageUrl =
          typeof banner.data.imageUrl === "string" ? banner.data.imageUrl : "";
        const name =
          typeof banner.data.name === "string" ? banner.data.name : "";
        html += `<div style="margin-top: 10px;"><img width="${imageWidth}" style="width: ${imageWidth}px; height: auto;" src="${imageUrl}" alt="${name}"></div>`;
      }
    }

    if (this.selectedAddons.additionalBannerId) {
      const additionalBanner = this.availableAddons.banners.find(
        (b) => b.addonId === this.selectedAddons.additionalBannerId,
      );
      if (additionalBanner) {
        const imageWidth =
          typeof additionalBanner.data.imageWidth === "number" ||
          typeof additionalBanner.data.imageWidth === "string"
            ? String(additionalBanner.data.imageWidth)
            : "auto";
        const imageUrl =
          typeof additionalBanner.data.imageUrl === "string"
            ? additionalBanner.data.imageUrl
            : "";
        const name =
          typeof additionalBanner.data.name === "string"
            ? additionalBanner.data.name
            : "";
        html += `<div style="margin-top: 10px;"><img width="${imageWidth}" style="width: ${imageWidth}px; height: auto;" src="${imageUrl}" alt="${name}"></div>`;
      }
    }

    if (this.selectedAddons.disclaimerId) {
      const disclaimer = this.availableAddons.disclaimers.find(
        (d) => d.addonId === this.selectedAddons.disclaimerId,
      );
      if (disclaimer) {
        const htmlContent =
          typeof disclaimer.data.htmlContent === "string"
            ? disclaimer.data.htmlContent
            : "";
        html += `<div style="margin-top: 10px; font-size: 12px; color: #666;">${htmlContent}</div>`;
      }
    }

    return html;
  }

  private updateEmailFieldState(): void {
    const emailField = this.basicInfoFields.find(
      (field) => field.model === "email",
    );
    if (emailField) {
      emailField.disabled = this.isEmailLocked;
    }
  }

  private ensureHttps(url: string): string {
    if (!url || url.trim() === "") {
      return url;
    }

    const trimmedUrl = url.trim();

    if (trimmedUrl.startsWith("http://") || trimmedUrl.startsWith("https://")) {
      return trimmedUrl;
    }

    return `https://${trimmedUrl}`;
  }

  editAddon(addonType: { plural: string }, addonId: string): void {
    const addonData = this.availableAddons[
      addonType.plural as keyof AvailableAddons
    ]?.find((item) => item.addonId === addonId);
    if (!addonData) {
      return;
    }

    let component: ComponentType<unknown>;

    switch (addonType.plural) {
      case "links":
        component = LinkEditComponent;
        break;
      case "banners":
        component = BannerEditComponent;
        break;
      case "disclaimers":
        component = DisclaimerEditComponent;
        break;
      default:
        return;
    }

    const dialogRef = this.dialog.open(component, {
      width: "600px",
      disableClose: true,
      data: {
        [addonType.plural.slice(0, -1)]: {
          addonId: addonData.addonId,
          ...addonData.data,
        },
      },
    });

    dialogRef.afterClosed().subscribe(() => {});
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }
}
